// ОТРИСОВКА РАЗДЕЛОВ BITRIX на карточках компании, контакта и судна.
//
// Раскладка (порядок разделов и полей) — данные, и живёт в api/entityLayouts.ts. Здесь —
// только вид: сворачиваемый раздел «как в Bitrix» и перевод сырого значения Bitrix в
// строку FieldRow. Строки рисует тот же FieldRow, что на карточке сделки: зеркальная
// капсула, подписи, «Править» (которого у зеркальных полей нет) — всё общее.
//
// Пустое поле — серый прочерк, а не «не заполнено»: в разделе на 47 полей (короткая
// верификация) слова в каждой пустой строке превращаются в стену текста, прочерк
// читается как в Bitrix.
//
// Файлы показываются счётом («файл ×2»), без ссылки: файл лежит в Bitrix, его адрес
// (showUrl) — относительный путь чужого портала, и рисовать ссылку, которая ведёт в
// никуда без сессии Bitrix, хуже честного «файл в Bitrix».
import { useState } from 'react'
import type { ReactNode } from 'react'

import type {
  ПолеМакета, РазделМакета,
} from '../api/entityLayouts'
import {
  РАЗДЕЛ_ВСЕГДА, деньгиBitrix, заполненоПолей, пустоеЗначение, ссылкаCrm, файлыBitrix,
} from '../api/entityLayouts'
import type { FieldsApi } from '../api/hooks'
import type { RefsApi } from '../lib/refs'
import type { CardField } from '../components/card/FieldRow'
import { FieldRow } from '../components/card/FieldRow'
import { EntityLink } from '../components/EntityLink'
import { fmtDate, fmtMoney, fmtText, multiField, plural } from '../lib/format'

import '../pages/cardEntities.css'

/** Телефоны/почты: их бывает несколько, тип Bitrix отдаёт кодом (WORK, MOBILE). */
export function MultiValues({ value, href }: { value: unknown; href?: (v: string) => string }) {
  const rows = multiField(value)
  if (rows.length === 0) return null
  return (
    <>
      {rows.map((r, i) => (
        <div key={`${r.value}-${i}`}>
          {href ? <a href={href(r.value)}>{r.value}</a> : r.value}
          {r.type && <span className="hint" style={{ marginLeft: 6 }}>{r.type}</span>}
        </div>
      ))}
    </>
  )
}

/** Серый прочерк пустого поля — как в Bitrix. */
export const ПРОЧЕРК = <span className="frow-empty">—</span>

const строкой = (список: ReactNode[]): ReactNode =>
  список.map((x, i) => <div key={i}>{x}</div>)

/**
 * Готовый вид значения по типу Bitrix. Пустоту сюда не приносят (см. строкаМакета).
 * Ловушки закреплены в entityLayouts.ts: enum приходит ID, деньги — '515000|RUB',
 * '0' у ссылок — «нет связи» (отсеян как пустота ещё до этого вызова).
 */
function видЗначения(
  поле: ПолеМакета, тип: string, v: unknown, f: FieldsApi, refs: RefsApi,
): ReactNode {
  const массив = Array.isArray(v) ? v : [v]
  switch (тип) {
    case 'crm_multifield':
      return (
        <MultiValues
          value={v}
          href={поле.code === 'PHONE'
            ? (т) => `tel:${т.replace(/[^+\d]/g, '')}`
            : поле.code === 'EMAIL' ? (т) => `mailto:${т}` : undefined}
        />
      )
    case 'enumeration':
    case 'crm_status':
      return массив.map((x) => fmtText(f.enumText(поле.code, x))).join(', ')
    case 'employee':
    case 'user':
      return строкой(массив.map((x) => refs.userName(String(x))))
    case 'crm': {
      const ссылки = массив.map((x) => {
        const с = ссылкаCrm(x, поле.crmTarget)
        return с && с.kind
          ? <EntityLink kind={с.kind} id={с.id} resolve />
          : <span className="mono">{fmtText(x)}</span>
      })
      return строкой(ссылки)
    }
    case 'file': {
      const файлы = файлыBitrix(v)
      if (файлы.length === 0) return ПРОЧЕРК
      return (
        <span title="Файл хранится в Bitrix — из CRM он не открывается">
          {файлы.length === 1
            ? 'файл'
            : `${файлы.length} ${plural(файлы.length, 'файл', 'файла', 'файлов')}`}
          <span className="hint" style={{ marginLeft: 6 }}>в Bitrix</span>
        </span>
      )
    }
    case 'date':
      return fmtDate(String(v ?? ''))
    case 'datetime':
      return fmtDate(String(v ?? ''), true)
    case 'money': {
      const д = деньгиBitrix(v)
      return д ? fmtMoney(д.сумма, д.валюта) : fmtText(v)
    }
    case 'url':
      return строкой(массив.map((x) => {
        const с = String(x ?? '')
        return /^https?:\/\//i.test(с)
          ? <a href={с} target="_blank" rel="noreferrer">{с}</a>
          : fmtText(с)
      }))
    case 'boolean':
      return v === true || v === 'Y' || v === '1' || v === 1 ? 'да' : 'нет'
    case 'double':
      return <span className="mono">{строкой(массив.map((x) => fmtText(x)))}</span>
    default:
      // string и всё незнакомое: множественное — построчно, одиночное — текстом.
      return Array.isArray(v) ? строкой(массив.map((x) => fmtText(x))) : fmtText(v)
  }
}

/**
 * Поле макета → строка FieldRow. Подпись — из боевого /fields (titleOr), тип — тоже
 * оттуда, когда справочник знает поле (переименовали/поменяли тип в Bitrix — доедет
 * само); запасные — из снимка раскладки. Все поля зеркальные: капсула, без правки.
 */
export function строкаМакета(
  поле: ПолеМакета, запись: Record<string, unknown>, f: FieldsApi, refs: RefsApi,
): CardField {
  const meta = f.meta(поле.code)
  const тип = meta?.type || поле.type
  const v = запись[поле.code]
  const пусто = пустоеЗначение(v, тип)
  return {
    code: поле.code,
    label: f.titleOr(поле.code, поле.label),
    type: 'text',
    value: пусто ? null : v,
    origin: 'bitrix',
    readOnly: true,
    display: пусто ? ПРОЧЕРК : видЗначения(поле, тип, v, f, refs),
    hint: поле.hint,
  }
}

// ─────────────────────── сворачиваемый раздел «как в Bitrix» ───────────────────────

interface СекцияProps {
  раздел: РазделМакета
  запись: Record<string, unknown>
  f: FieldsApi
  refs: RefsApi
  /**
   * Особые строки по коду поля: REQUISITES, CONTACT, ID и прочее, что не лежит в записи
   * простым значением. null — строку не показывать вовсе.
   */
  особые?: Record<string, CardField | null>
  /** Открыть раздел сразу. Умолчание: открыт, когда в нём есть хоть одно значение. */
  открыт?: boolean
  /** Дополнение под строками (оговорка, подсказка). */
  children?: ReactNode
}

export function СекцияBitrix({ раздел, запись, f, refs, особые, открыт, children }: СекцияProps) {
  const строки: CardField[] = []
  let особыхЗаполнено = 0
  for (const поле of раздел.поля) {
    if (особые && поле.code in особые) {
      const своя = особые[поле.code]
      if (своя === null) continue
      строки.push(своя)
      if (своя.value !== null && своя.value !== undefined && своя.value !== '') особыхЗаполнено += 1
      continue
    }
    строки.push(строкаМакета(поле, запись, f, refs))
  }
  const заполнено = заполненоПолей(раздел, запись) + особыхЗаполнено
  const [открыта, setОткрыта] = useState(открыт ?? заполнено > 0)

  return (
    <section className="fieldsec centity-sec">
      <button
        type="button"
        className="centity-toggle"
        aria-expanded={открыта}
        onClick={() => setОткрыта((б) => !б)}
      >
        <span className="centity-chevron" aria-hidden>{открыта ? '▾' : '▸'}</span>
        <span className="fieldsec-title centity-title">{раздел.title}</span>
        <span className="centity-count">
          {заполнено > 0
            ? `${заполнено} из ${строки.length} ${plural(строки.length, 'поля', 'полей', 'полей')}`
            : 'пусто'}
        </span>
      </button>
      {открыта && (
        <div className="centity-rows">
          {строки.map((поле) => <FieldRow key={поле.code} field={поле} />)}
          {children}
        </div>
      )}
    </section>
  )
}

interface РазделыProps {
  макет: РазделМакета[]
  запись: Record<string, unknown>
  f: FieldsApi
  refs: RefsApi
  особые?: Record<string, CardField | null>
  /**
   * Прятать разделы без единого значения (карточка человека: верификации и документы
   * БТО — судовые, пустыми они только шумят). Первый раздел (РАЗДЕЛ_ВСЕГДА) не прячется.
   */
  прятатьПустые?: boolean
  /** Заметка над разделами: почему поля Bitrix могли не показаться (зеркало отстало…). */
  заметка?: string
}

/** Разделы Bitrix одной раскладкой: порядок — как на живой карточке cp.cir.cx. */
export function РазделыBitrix({ макет, запись, f, refs, особые, прятатьПустые, заметка }: РазделыProps) {
  const видимые = макет.filter((раздел) => {
    if (!прятатьПустые || раздел.key === РАЗДЕЛ_ВСЕГДА) return true
    return заполненоПолей(раздел, запись) > 0
  })
  return (
    <>
      {заметка && <div className="hint centity-note">{заметка}</div>}
      {видимые.map((раздел, i) => (
        <СекцияBitrix
          key={раздел.key}
          раздел={раздел}
          запись={запись}
          f={f}
          refs={refs}
          особые={i === 0 ? особые : undefined}
          открыт={i === 0 ? true : undefined}
        />
      ))}
      {прятатьПустые && видимые.length < макет.length && (
        <div className="hint centity-note">
          Пустые судовые разделы ({макет.length - видимые.length}) скрыты — у этой записи
          в них нет ни одного значения.
        </div>
      )}
    </>
  )
}
